import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CContainer, CRow, CCol, CCard, CCardHeader, CCardBody, CSpinner, CButton, CBadge } from '@coreui/react';
import api from '../../api/axiosInstance';
import MathRenderer from '../../components/MathRenderer';

const NotesQuestionsList = () => {
  const { section, title, noteId, chapterName, topicName } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const displaySection = section ? section.replace(/-/g, ' ') : '';
  const displayTitle = title ? title.replace(/-/g, ' ') : '';
  const displayChapter = chapterName ? chapterName.replace(/-/g, ' ') : '';
  const displayTopic = topicName ? topicName.replace(/-/g, ' ') : '';

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const apiChapter = chapterName ? chapterName.replace(/-/g, ' ') : '';
        const apiTopic = topicName ? topicName.replace(/-/g, ' ') : '';

        const res = await api.get(
          `/api/Notes/GetQuestionsByNoteChapterAndTopic/${section}/${title}/${noteId}/${encodeURIComponent(apiChapter)}/${encodeURIComponent(apiTopic)}`,
          { skipAuthCheck: true }
        );
        
        setQuestions(res.data || []);
      } catch (err) {
        console.error('Error fetching questions:', err);
      } finally {
        setLoading(false);
      }
    };

    if (noteId && chapterName && topicName) {
      fetchQuestions();
    }
  }, [section, title, noteId, chapterName, topicName]);

  const handleViewQuestion = (questionId, customUrl) => {
    navigate(`/content/view-question/${questionId}/${encodeURIComponent(customUrl || displayTopic.replace(/\s+/g, '-'))}`);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
        <p className="mt-2 fw-semibold">Loading Questions...</p>
      </div>
    );
  }

  return (
    <div className="pb-5 bg-light min-vh-100">
      {/* HEADER BANNER */}
      <div style={{ backgroundColor: 'rgb(0, 33, 71)', color: 'white', padding: '35px 0' }}>
        <CContainer>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <span className="badge bg-warning text-dark me-2 text-uppercase">{displaySection}</span>
              <span className="badge bg-light text-primary text-capitalize">{displayTitle}</span>
              <h2 className="fw-bold text-white mt-2 mb-1 text-capitalize">{displayTopic}</h2>
              <p className="mb-0 text-light opacity-75">Chapter: <span className="fw-semibold">{displayChapter}</span></p>
            </div>
            <CButton color="light" variant="outline" onClick={() => navigate(-1)}>
              Go Back
            </CButton>
          </div>
        </CContainer>
      </div>

      {/* QUESTIONS LIST SECTION */}
      <CContainer className="mt-4">
        <CRow>
          <CCol md={12}>
            <CCard className="shadow-sm border-0">
              <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center py-3">
                <span className="fs-5 text-dark">Practice Questions</span>
                <CBadge color="primary" shape="rounded-pill" className="px-3 py-2">
                  Total Questions: {questions.length}
                </CBadge>
              </CCardHeader>
              <CCardBody className="p-4">
                {questions.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <h5>No questions available for this topic yet.</h5>
                    <p>Please check back later or choose another topic.</p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {questions.map((q, index) => (
                      <CCard key={q.id || index} className="border shadow-none mb-3">
                        <CCardBody className="d-flex justify-content-between align-items-center p-3">
                          <div className="d-flex align-items-baseline gap-3 w-100">
                            <span className="fw-bold text-primary fs-5 flex-shrink-0">{index + 1}.</span>
                            
                            {/* Simple clean wrapper for math */}
                            <div className="math-wrapper flex-grow-1 text-dark">
                              <MathRenderer htmlContent={q.questionText || q.text} />
                              {q.year && <span className="badge bg-secondary mt-2 d-inline-block">{q.year}</span>}
                            </div>
                          </div>
                          
                          <CButton
                            color="primary"
                            size="sm"
                            className="px-3 text-nowrap ms-3 flex-shrink-0"
                            onClick={() => handleViewQuestion(q.id, q.customeUrl)}
                          >
                            View Solution
                          </CButton>
                        </CCardBody>
                      </CCard>
                    ))}
                  </div>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default NotesQuestionsList;